import { inject, Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { BUTTON_VARIANT } from '@discocil/fv-components';
import { TranslateService } from '@ngx-translate/core';
import { Observable, first } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { GlobalPromptService } from 'src/app/shared/services/global-prompt/global-prompt.service';
import { GlobalSnackbarService } from 'src/app/shared/services/global-snackbar/global-snackbar.service';
import { WaiterAuthService } from 'src/app/auth/services/waiter-auth/waiter-auth.service';
import { IAuthWaiter } from 'src/app/auth/interfaces/waiter-auth.interface';
import { Worker } from '@features/service/domain/models/worker.model';
import { unauthorizedAlertError } from 'src/app/config/global-snackbar.config';

@Injectable({ providedIn: 'root' })
export class RequestWorkerPasswordUseCase {
  private readonly globalPromptService = inject(GlobalPromptService);
  private readonly globalSnackbarService = inject(GlobalSnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly waiterAuthService = inject(WaiterAuthService);
  private readonly destroyRef = inject(DestroyRef);

  execute(worker: Worker): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.globalPromptService.open({
        icon: 'far fa-lock-alt',
        message: this.translateService.instant('waiter_page.password_modal.message'),
        input: {
          type: 'hiddenNumber',
          placeholder: '',
          maxLength: 4,
        },
        validators: [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(4),
          Validators.pattern('^[0-9]{4}$'),
        ],
        button: {
          icon: 'fa fal fa-long-arrow-right',
          handler: (password: string) => {
            const waiterAuth: IAuthWaiter = {
              waiter_id: worker.id,
              password: password,
            };

            this.waiterAuthService
              .signInWaiter(waiterAuth, worker)
              .pipe(
                first(),
                takeUntilDestroyed(this.destroyRef)
              )
              .subscribe({
                next: (success: boolean) => {
                  if (success) {
                    this.globalPromptService.close();
                    observer.next(true);
                    observer.complete();
                  } else {
                    this.globalSnackbarService.open(unauthorizedAlertError);
                  }
                },
                error: (error: Error) => {
                  this.globalSnackbarService.open(unauthorizedAlertError);
                  observer.error(error);
                },
              });
          },
          variant: BUTTON_VARIANT.icononly,
        },
        errorMessages: [
          {
            key: 'required',
            message: this.translateService.instant('waiter_page.fv_prompt.error_msg_required'),
          },
          {
            key: 'minLength',
            message: this.translateService.instant('waiter_page.fv_prompt.error_msg_min_length'),
          },
          {
            key: 'maxlength',
            message: this.translateService.instant('waiter_page.fv_prompt.error_msg_max_length'),
          },
          {
            key: 'pattern',
            message: this.translateService.instant('waiter_page.fv_prompt.error_msg_pattern'),
          },
        ],
      });
    });
  }
}

